import styled, { css } from "styled-components";
import { theme } from "../..";
import ButtonProps from "./Button.types";

const buttonReset = css`
    padding: 0;
    font: inherit;
    color: inherit;
    background-color: transparent;
    cursor: pointer;
    user-select: none;
    appearance: none;
`;

const sharedButtonIconSize = css<ButtonProps>`
    width: ${({ iconSize }) => iconSize && `${iconSize}px`};
    max-width: ${({ theme }) => theme.elements.button.height};
    height: ${({ iconSize }) => iconSize && `${iconSize}px`};
    max-height: ${({ theme }) => theme.elements.button.height};
`;

const sharedLoadingIconSize = css<ButtonProps>`
    width: ${({ loadingIconSize }) =>
        loadingIconSize && `${loadingIconSize}px` <= theme.elements.button.height
            ? `${loadingIconSize}px`
            : theme.elements.button.height};
    height: ${({ loadingIconSize }) =>
        loadingIconSize && `${loadingIconSize}px` <= theme.elements.button.height
            ? `${loadingIconSize}px`
            : theme.elements.button.height};
`;

export const LoadingIconHolder = styled.svg`
    ${sharedLoadingIconSize};

    image {
        ${sharedLoadingIconSize};
    }
`;

// Below mask solution is used to dynamically change the icon color depending on how it is rendered.
// More on this solution: https://stackoverflow.com/questions/24933430/img-src-svg-changing-the-styles-with-css
export const ButtonIconHolder = styled.div<ButtonProps>`
    background-color: black;
    mask: ${(props) => `url(${props.iconURL}) no-repeat center / contain`};
    ${sharedButtonIconSize};
`;

export const SvgIconHolder = styled.div<ButtonProps>`
    display: inline-flex;
    align-items: center;

    svg {
        ${sharedButtonIconSize};
    }
`;

// TODO: box-shadow property is missing in many variantions below (other colours aren't in the desing system yet)
// TODO: Source styles from the elements key in the theme obj (like done in Form components) (started but not finished)
export const DefaultButton = styled.button<ButtonProps>`
    ${buttonReset};
    display: flex;
    align-items: center;
    justify-content: center;
    border: solid transparent;
    border-radius: ${({ theme }) => theme.elements.button.borderRadius};
    padding: ${({ theme }) => theme.elements.button.padding};
    font-size: ${({ theme }) => theme.elements.button.fontSize};
    font-weight: ${({ theme }) => theme.typography.weights[400]};
    height: ${({ theme }) => theme.elements.button.height};
    min-width: ${({ theme }) => theme.elements.button.minWidth};
    box-shadow: 0 3px 3px 0 ${({ theme }) => theme.colors["black.25"]};

    ${({ isLoading }) =>
        isLoading &&
        css`
            pointer-events: none;
            color: transparent !important;

            ${ButtonIconHolder} {
                opacity: 0 !important;
            }

            ${SvgIconHolder} {
                opacity: 0 !important;
            }

            ${LoadingIconHolder} {
                position: absolute;
            }
        `};

    ${({ fullWidth }) => fullWidth && "width: 100%"};

    ${({ iconURL, iconSvg, iconPosition }) => {
        if (iconURL || iconSvg) {
            switch (iconPosition) {
                case "left":
                    return css`
                        flex-flow: row;

                        ${ButtonIconHolder}, ${SvgIconHolder} {
                            margin-right: 12px;
                        }
                    `;
                case "right":
                    return css`
                        flex-flow: row-reverse;

                        ${ButtonIconHolder}, ${SvgIconHolder} {
                            margin-left: 12px;
                        }
                    `;
                default:
                    return null;
            }
        } else {
            return null;
        }
    }};

    ${({ color, variant }) => {
        switch (color) {
            case "white":
                switch (variant) {
                    case "primary":
                        return css`
                            background-color: ${({ theme }) => theme.colors["white.100"]};
                            color: ${({ theme }) => theme.colors["black.100"]};
                            border-width: 1px;

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors["black.100"]};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors["black.100"]};
                            }

                            &:hover {
                                background-color: ${({ theme }) => theme.colors.blue.light}; // #bfccff
                                box-shadow: 0 3px 12px 0 ${({ theme }) => theme.colors["black.50"]};
                            }

                            &:focus-visible {
                                background-color: ${({ theme }) => theme.colors["white.100"]}; //#fff
                                box-shadow: 0 0 12px 0 ${({ theme }) => theme.colors["black.25"]};
                                outline: 2px solid ${({ theme }) => theme.colors.blue.light};
                            }

                            &:active {
                                background-color: ${({ theme }) => theme.colors.blue.base}; //#8099fc
                                border-color: transparent;
                                box-shadow: 0 0 12px 0 ${({ theme }) => theme.colors["black.25"]};
                            }

                            &:disabled {
                                pointer-events: none;
                                color: ${({ theme }) => theme.colors["black.100"]};
                                background-color: ${({ theme }) => theme.colors.disabled}; //#ffffff40;
                                border-color: transparent;
                                box-shadow: 0 0 12px 0 ${({ theme }) => theme.colors["black.25"]};
                            }
                        `;
                    case "secondary":
                        return css`
                            color: ${({ theme }) => theme.colors["white.75"]};
                            border-color: ${({ theme }) => theme.colors["white.75"]};
                            border-width: 1px;

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors["white.75"]};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors["white.75"]};
                            }

                            &:hover {
                                border-color: ${({ theme }) => theme.colors["white.100"]};
                                background-color: ${({ theme }) => theme.colors["white.10"]};
                                box-shadow: 0 3px 12px 0 ${({ theme }) => theme.colors["black.50"]};
                            }

                            &:focus-visible {
                                color: ${({ theme }) => theme.colors["white.100"]};
                                outline: 2px solid ${({ theme }) => theme.colors.blue.light};
                                border-color: transparent;

                                ${ButtonIconHolder} {
                                    background-color: ${({ theme }) => theme.colors["white.100"]};
                                }

                                ${SvgIconHolder} {
                                    stroke: ${({ theme }) => theme.colors["white.100"]};
                                }
                            }

                            &:active {
                                color: ${({ theme }) => theme.colors["white.100"]};
                                background-color: ${({ theme }) => theme.colors.darkGray75};
                                border-color: ${({ theme }) => theme.colors["white.100"]};
                                box-shadow: 0 0 12px 0 ${({ theme }) => theme.colors["black.25"]};

                                ${ButtonIconHolder} {
                                    background-color: ${({ theme }) => theme.colors["white.100"]};
                                }

                                ${SvgIconHolder} {
                                    stroke: ${({ theme }) => theme.colors["white.100"]};
                                }
                            }

                            &:disabled {
                                pointer-events: none;
                                box-shadow: none;
                                color: ${({ theme }) => theme.colors["white.25"]};
                                border-color: ${({ theme }) => theme.colors["white.25"]};

                                ${ButtonIconHolder} {
                                    background-color: ${({ theme }) => theme.colors["white.25"]};
                                }

                                ${SvgIconHolder} {
                                    stroke: ${({ theme }) => theme.colors["white.25"]};
                                }
                            }
                        `;
                    case "tertiary":
                        return css`
                            color: ${({ theme }) => theme.colors["white.75"]};
                            border-width: 1px;
                            box-shadow: none;

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors["white.75"]};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors["white.75"]};
                            }

                            &:hover {
                                color: ${({ theme }) => theme.colors["white.100"]};
                                background-color: ${({ theme }) => theme.colors.darkGray50};

                                ${ButtonIconHolder} {
                                    background-color: ${({ theme }) => theme.colors["white.100"]};
                                }

                                ${SvgIconHolder} {
                                    stroke: ${({ theme }) => theme.colors["white.100"]};
                                }
                            }

                            &:focus-visible {
                                outline: 2px solid ${({ theme }) => theme.colors.blue.light};

                                ${ButtonIconHolder} {
                                    background-color: ${({ theme }) => theme.colors["white.100"]};
                                }

                                ${SvgIconHolder} {
                                    stroke: ${({ theme }) => theme.colors["white.100"]};
                                }
                            }

                            &:active {
                                color: ${({ theme }) => theme.colors["white.100"]};
                                background-color: ${({ theme }) => theme.colors["black.75"]};
                                border-color: transparent;

                                ${ButtonIconHolder} {
                                    background-color: ${({ theme }) => theme.colors["white.100"]};
                                }

                                ${SvgIconHolder} {
                                    stroke: ${({ theme }) => theme.colors["white.100"]};
                                }
                            }

                            &:disabled {
                                pointer-events: none;
                                box-shadow: none;
                                border-color: transparent;
                                color: ${({ theme }) => theme.colors["white.25"]};

                                ${ButtonIconHolder} {
                                    background-color: ${({ theme }) => theme.colors["white.25"]};
                                }

                                ${SvgIconHolder} {
                                    stroke: ${({ theme }) => theme.colors["white.25"]};
                                }
                            }
                        `;
                    default:
                        return null;
                }
            case "black":
                switch (variant) {
                    case "primary":
                        return css`
                            background-color: ${({ theme }) => theme.colors["black.100"]};
                            color: ${({ theme }) => theme.colors["white.100"]};
                            border-width: 1px;

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors["white.100"]};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors["white.100"]};
                            }

                            &:hover {
                                background-color: ${({ theme }) => theme.colors["black.75"]};
                                border-color: ${({ theme }) => theme.colors["black.100"]};
                                box-shadow: 0 0 12px 0 ${({ theme }) => theme.colors["black.50"]};
                            }

                            &:focus {
                                background-color: ${({ theme }) => theme.colors["black.50"]};
                                border-color: ${({ theme }) => theme.colors.info.strong};
                            }

                            &:active {
                                background-color: ${({ theme }) => theme.colors["black.100"]};
                                border-color: transparent;
                            }
                        `;
                    case "secondary":
                        return css`
                            background-color: transparent;
                            color: ${({ theme }) => theme.colors["black.100"]};
                            border-width: 1px;
                            border-color: ${({ theme }) => theme.colors["black.100"]};

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors["black.100"]};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors["black.100"]};
                            }

                            &:hover {
                                background-color: ${({ theme }) => theme.colors["black.10"]};
                                box-shadow: 0 0 12px 0 ${({ theme }) => theme.colors["black.50"]};
                            }

                            &:focus {
                                background-color: ${({ theme }) => theme.colors["black.25"]};
                                border-color: ${({ theme }) => theme.colors.info.strong};
                            }

                            &:active {
                                color: ${({ theme }) => theme.colors["white.100"]};
                                background-color: ${({ theme }) => theme.colors["black.100"]};
                                border-color: transparent;
                            }
                        `;
                    case "tertiary":
                        return css`
                            background-color: transparent;
                            color: ${({ theme }) => theme.colors["black.100"]};

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors["black.100"]};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors["black.100"]};
                            }

                            &:hover {
                                box-shadow: 0 0 12px 0 ${({ theme }) => theme.colors["black.50"]};
                            }

                            &:focus {
                                background-color: ${({ theme }) => theme.colors["black.25"]};
                                border-color: ${({ theme }) => theme.colors.info.strong};
                            }

                            &:active {
                                background-color: ${({ theme }) => theme.colors["black.25"]};
                                border-color: transparent;
                            }
                        `;
                    default:
                        return null;
                }
            case "blue":
                switch (variant) {
                    case "primary":
                        return css`
                            background-color: ${({ theme }) => theme.colors.blue.light};
                            color: ${({ theme }) => theme.colors["black.100"]};
                            border-width: 1px;

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors["black.100"]};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors["black.100"]};
                            }

                            &:hover {
                                background-color: ${({ theme }) => theme.colors.blue.base};
                            }

                            &:active {
                                background-color: ${({ theme }) => theme.colors["white.100"]};
                                border-color: ${({ theme }) => theme.colors["white.100"]};
                            }
                        `;
                    case "secondary":
                        return css`
                            background-color: transparent;
                            color: ${({ theme }) => theme.colors.blue.light};
                            border-width: 1px;
                            border-color: ${({ theme }) => theme.colors.blue.light};

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors.blue.light};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors.blue.light};
                            }

                            &:hover {
                                background-color: ${({ theme }) => theme.colors.blue.base};
                            }

                            &:focus {
                                border-color: ${({ theme }) => theme.colors.info.base};
                            }
                        `;
                    case "tertiary":
                        return css`
                            background-color: transparent;
                            color: ${({ theme }) => theme.colors.blue.light};
                            border-width: 1px;

                            ${ButtonIconHolder} {
                                background-color: ${({ theme }) => theme.colors.blue.light};
                            }

                            ${SvgIconHolder} {
                                stroke: ${({ theme }) => theme.colors.blue.light};
                            }

                            &:hover {
                            }

                            &:focus {
                            }
                        `;
                    default:
                        return null;
                }
            default:
                return null;
        }
    }}
`;
